import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import AlertDialog from '../../shared/alertDialog';
import CourseDetailsCard from '../../shared/courseDetailsCard';
import SectionTitle from '../../shared/sectionTitle';
import EnrolledEmployeeCard from '../../shared/enrolledEmployeeCard';
import NotificationCard from '../../shared/notificationCard';
import CustomButton from '../../shared/customButton';
import AssignUsersDialog from '../../shared/assignUsersDialog';
import Filter from '../../shared/filter';
import CustomFormDrawer from '../../shared/customFormDrawer';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { GENERAL_CATEGORY_ID } from '../../../constants/tags';
import { ATTRIBUTE_TYPES_MAP } from '../attributesPage/config';
import { PARAMS, COURSES_DEFAULT_PARAMS } from '../../../constants/pages';
import {
  api_course,
  api_course_users_disenroll,
  api_course_users_enroll,
  API_COURSES,
  api_course_users,
} from '../../../constants/apiRoutes';
import {
  isArrayEmpty,
  isObjectEmpty,
  trimString,
  isUserDeactivated,
  getObjectToNumberArray,
} from '../../../utility/helpers';
import {
  hasAuthPermissionCheck,
  hasAuthPermissionsCheck,
  hasScopedAuthPermissionCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import http from '../../../utility/http';
import {
  showSuccessMessage,
  manageQuery,
  parameterizeQuery,
} from '../../../utility/uiUtils';
import { onSaveCreatableTag } from '../../../utility/tagUtils';
import {
  prepareCourseLevels,
  prepareEnrollmentStatuses,
  prepareEnrollmentStatus,
  prepareCourseData,
} from '../../../utility/courseUtils';
import { getEditCourseFields, prepareCourseActions } from './config';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';

const styles = ({ spacing }) => ({
  sectionTitleWrapper: {
    marginTop: spacing(8),
    marginBottom: spacing(4),
  },
  enrolledUser: {
    marginBottom: spacing(4),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(4),
    width: '100%',
  },
  enrollButton: {
    marginLeft: 'auto',
  },
  status: {
    width: 200,
  },
});

class CourseDetailsPage extends PureComponent {
  state = {
    isLoading: false,
    isFilterLoading: false,
    enrollToCourseDialogOpened: false,
    disenrollDialogOpened: false,
    disenrollUserId: null,
    isEditCourseOpened: false,
    status: 0,
  };

  componentDidMount() {
    const {
      matchedCourseId,
      accessControl,
      getCourse,
      getAttributesWithQuestions,
      getTagsCategories,
    } = this.props;
    const canEdit = hasAuthPermissionCheck(
      PERMISSION_CODES.LEARNING_EDIT,
      accessControl
    );

    if (canEdit) {
      getTagsCategories();
      getAttributesWithQuestions({
        [PARAMS.EXCLUDE_ATTRIBUTE_TYPE]: ATTRIBUTE_TYPES_MAP.ENPS.id,
        [PARAMS.IS_PRIVATE_ATTRIBUTE]: false,
      });
    }

    return Promise.all([
      getCourse(matchedCourseId),
      this.getCourseUsers(matchedCourseId),
    ]).then(() => {
      this.setState({ isLoading: false });
      const { VisitCDP } = EVENT_ACTION_TYPES;
      tagManagerDataLayer(VisitCDP.action, VisitCDP.name);
    });
  }

  componentWillUnmount() {
    const {
      clearCourse,
      clearCourseEnrolledUsers,
      clearAttributesWithQuestions,
      clearTagsCategories,
      clearAllUsers,
    } = this.props;
    clearTagsCategories();
    clearCourse();
    clearCourseEnrolledUsers();
    clearAttributesWithQuestions();
    clearAllUsers();
  }

  getCourseUsers = courseId => {
    const { location, getCourseEnrolledUsers } = this.props;
    const { status } = this.state;

    const query = manageQuery({
      key: 'status',
      value: status,
      shouldDelete: !status,
      query: location.search,
    });

    return getCourseEnrolledUsers(courseId, parameterizeQuery(query));
  };

  onGoToPersonCourses = userId => () => {
    const { navigate } = this.props;
    navigate(`/people/${userId}/learning`);
  };

  toggleDeleteCourseDialog = () => {
    const { setDialogVisibility, dialogs } = this.props;
    const { forceDeleteCourseDialogOpened } = dialogs;

    setDialogVisibility({
      dialogName: 'forceDeleteCourseDialog',
      opened: !forceDeleteCourseDialogOpened,
    });
  };

  toggleEditCourseDialog = () =>
    this.setState(prevState => ({
      isEditCourseOpened: !prevState.isEditCourseOpened,
    }));

  toggleDisenrollDialog = userId =>
    this.setState(prevState => ({
      disenrollDialogOpened: !prevState.disenrollDialogOpened,
      disenrollUserId: userId || null,
    }));

  toggleEnrollToCourseDialog = (shouldClearUsers = true) => {
    const { clearAllUsers } = this.props;

    this.setState(
      prevState => ({
        enrollToCourseDialogOpened: !prevState.enrollToCourseDialogOpened,
      }),
      () => {
        if (shouldClearUsers) {
          clearAllUsers();
        }
      }
    );
  };

  handleEnrollToCourseDialog = async () => {
    const { course, getAllUsers } = this.props;

    const alreadyEnrolled = await http
      .get(api_course_users(course.id), {
        params: { ...COURSES_DEFAULT_PARAMS },
      })
      .then(({ data }) => getObjectToNumberArray(data.results));

    const params = {
      [PARAMS.EXCLUDE]: alreadyEnrolled,
      [PARAMS.RBAC_SCOPE_PERMISSION]: PERMISSION_CODES.LEARNING_MANAGE_USERS,
    };
    await getAllUsers(params);
    this.toggleEnrollToCourseDialog(false);
  };

  onEnrollToCourse = users => {
    const { course, translations } = this.props;

    return http
      .post(api_course_users_enroll(course.id), { users })
      .then(() => {
        showSuccessMessage(
          `${users.length} ${translations.successMessages.enroll}`
        );
        const { EnrollEmployeeOnCourse } = EVENT_ACTION_TYPES;
        tagManagerDataLayer(
          EnrollEmployeeOnCourse.action,
          EnrollEmployeeOnCourse.name,
          users.length
        );
      })
      .finally(() => {
        this.getCourseUsers(course.id);
        this.toggleEnrollToCourseDialog();
      });
  };

  onDeleteCourse = courseId => () => {
    const { translations, navigate } = this.props;

    return http.delete(`${API_COURSES}${courseId}/`).then(() => {
      this.toggleDeleteCourseDialog();
      showSuccessMessage(translations.deleteCourseDialog.deleteSuccess);
      navigate('/learning');
    }, this.toggleDeleteCourseDialog);
  };

  onChangeEnrollmentStatus = userId => statusId => {
    const { translations, course } = this.props;

    return http
      .patch(`${api_course_users(course.id)}${userId}/`, {
        status: statusId,
      })
      .then(() => {
        showSuccessMessage(translations.successMessages.status);
        return this.getCourseUsers(course.id);
      });
  };

  onChangeStatusFilter = newStatus => {
    const { course } = this.props;

    return this.setState({ status: newStatus, isFilterLoading: true }, () =>
      this.getCourseUsers(course.id).then(() =>
        this.setState({ isFilterLoading: false })
      )
    );
  };

  onSaveCourse = values => {
    const { translations, setCourse } = this.props;
    const { id, title, url, attribute, tags, level, description, cover_image } =
      values;

    const payload = new FormData();
    const sanitizedDescription = description ? trimString(description) : '';
    payload.append('title', title);
    payload.append('url', url);

    payload.append('description', sanitizedDescription || '');
    payload.append('level', level || '');
    if (!cover_image || cover_image instanceof File) {
      payload.append('cover_image', cover_image || '');
    }
    payload.append('attribute', attribute || '');
    if (!isArrayEmpty(tags)) {
      tags.forEach(tag => {
        payload.append('tags', tag);
      });
    } else {
      payload.append('tags', '');
    }

    return http.patch(api_course(id), payload).then(({ data }) => {
      showSuccessMessage(translations.successMessages.editCourse);
      setCourse(data);
    });
  };

  onDisenrollUser = () => {
    const { translations, course } = this.props;
    const { disenrollUserId } = this.state;
    if (disenrollUserId) {
      http
        .post(api_course_users_disenroll(course.id), {
          users: [disenrollUserId],
        })
        .then(() => {
          this.getCourseUsers(course.id);
          showSuccessMessage(
            translations.disenrollCourseDialog.disenrollSuccess
          );
        })
        .finally(() => {
          this.toggleDisenrollDialog();
        });
    }
  };

  onCreateTag = async tagName => {
    const { addMultipleTags } = this.props;
    const tags = await onSaveCreatableTag(tagName);
    const [tag] = tags.create;

    addMultipleTags({ categoryId: GENERAL_CATEGORY_ID, tags });

    return tag;
  };

  canAccessUserProfileCheck = userId => {
    const { auth, accessControl } = this.props;

    return isUserProfileAccessibleCheck(accessControl, auth.id, userId);
  };

  canManageEmployeeCheck = userId => {
    const { auth, accessControl } = this.props;

    return hasScopedAuthPermissionCheck(
      PERMISSION_CODES.LEARNING_MANAGE_USERS,
      accessControl,
      auth.id,
      userId
    );
  };

  render() {
    const {
      classes,
      translations,
      accessControl,
      course,
      enrolledUsers,
      dialogs,
      allAttributes,
      categories,
      allUsers,
    } = this.props;
    const {
      isLoading,
      disenrollDialogOpened,
      isEditCourseOpened,
      enrollToCourseDialogOpened,
      status,
      isFilterLoading,
    } = this.state;
    const { forceDeleteCourseDialogOpened } = dialogs;
    const [canEdit, canDelete, canManageUsers] = hasAuthPermissionsCheck(
      [
        PERMISSION_CODES.LEARNING_EDIT,
        PERMISSION_CODES.LEARNING_DELETE,
        PERMISSION_CODES.LEARNING_MANAGE_USERS,
      ],
      accessControl
    );

    const enrollmentStatuses = prepareEnrollmentStatuses(
      translations.enrollmentStatuses
    );
    const statusFilterOptions = [
      { id: 0, name: translations.all },
      ...enrollmentStatuses,
    ];

    const courseLevels = prepareCourseLevels(translations.levels);
    const courseActions = prepareCourseActions(
      translations.courseActions,
      canEdit,
      canDelete,
      this.toggleEditCourseDialog,
      this.toggleDeleteCourseDialog
    );
    const isStatusSelected = status > 0;

    return (
      <div>
        {!isLoading && (
          <>
            {!isObjectEmpty(course) && (
              <div>
                <SectionTitle
                  className={classes.sectionTitleWrapper}
                  title={translations.courseDetails}
                />
                <CourseDetailsCard
                  translations={translations.courseDetailsCard}
                  isReadOnly={!canEdit}
                  levels={courseLevels}
                  actions={courseActions}
                  course={course}
                />
              </div>
            )}
            {!isObjectEmpty(enrolledUsers) && (
              <div>
                <SectionTitle
                  className={classes.sectionTitleWrapper}
                  title={`${translations.enrolledUsers} (${enrolledUsers.results.length})`}
                />
                <div className={classes.actions}>
                  <div className={classes.status}>
                    <Filter
                      name={translations.status}
                      options={statusFilterOptions}
                      value={status}
                      onChange={this.onChangeStatusFilter}
                    />
                  </div>
                  {canManageUsers && (
                    <CustomButton
                      className={classes.enrollButton}
                      type="addDarkRoundedOutlined"
                      onClick={this.handleEnrollToCourseDialog}
                    >
                      {translations.enrollUser}
                    </CustomButton>
                  )}
                </div>
                <div>
                  {!isArrayEmpty(enrolledUsers.results) &&
                    enrolledUsers.results.map(user => (
                      <EnrolledEmployeeCard
                        key={`enrolled_user_${user.id}`}
                        className={classes.enrolledUser}
                        translations={translations}
                        user={user}
                        status={prepareEnrollmentStatus(
                          user.enrolment_status,
                          enrollmentStatuses
                        )}
                        statuses={enrollmentStatuses}
                        isReadOnly={!this.canManageEmployeeCheck(user.id)}
                        canAccessUserProfileCheck={this.canAccessUserProfileCheck(
                          user.id
                        )}
                        isStatusChangeDisabled={isUserDeactivated(user)}
                        onDisenroll={() => this.toggleDisenrollDialog(user.id)}
                        onChangeEnrollmentStatus={this.onChangeEnrollmentStatus(
                          user.id
                        )}
                        onGoToCourses={this.onGoToPersonCourses(user.id)}
                      />
                    ))}
                  {!isFilterLoading && isArrayEmpty(enrolledUsers.results) && (
                    <NotificationCard
                      content={
                        isStatusSelected
                          ? translations.noSearchResultsMessage
                          : translations.noEnrolledEmployees
                      }
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
        <CustomFormDrawer
          translations={translations.editCourseForm}
          initialData={prepareCourseData(course)}
          isOpened={isEditCourseOpened}
          fields={getEditCourseFields(this.onCreateTag)}
          allAttributes={allAttributes}
          categories={categories}
          levels={courseLevels}
          hideDelete={!canDelete}
          onClose={this.toggleEditCourseDialog}
          onSave={this.onSaveCourse}
          onDelete={this.toggleDeleteCourseDialog}
          isInitialValid
          hasCancelButton
        />
        <AssignUsersDialog
          translations={translations.enrollToCourseDialog}
          isOpened={enrollToCourseDialogOpened}
          allUsers={allUsers}
          onCancel={this.toggleEnrollToCourseDialog}
          onSave={this.onEnrollToCourse}
          isMulty
          shouldResetSelection
        />
        <AlertDialog
          translations={translations.disenrollCourseDialog}
          isOpened={disenrollDialogOpened}
          onClose={this.toggleDisenrollDialog}
          onConfirm={this.onDisenrollUser}
        />
        <AlertDialog
          translations={translations.deleteCourseDialog}
          isOpened={forceDeleteCourseDialogOpened}
          onClose={this.toggleDeleteCourseDialog}
          onConfirm={this.onDeleteCourse(course.id)}
          isWarning
        />
      </div>
    );
  }
}

CourseDetailsPage.propTypes = {
  classes: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  matchedCourseId: PropTypes.string.isRequired,
  dialogs: PropTypes.object.isRequired,
  categories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  course: PropTypes.object.isRequired,
  enrolledUsers: PropTypes.shape({}).isRequired,
  allAttributes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  allUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setDialogVisibility: PropTypes.func.isRequired,
  getCourse: PropTypes.func.isRequired,
  setCourse: PropTypes.func.isRequired,
  clearCourse: PropTypes.func.isRequired,
  getAttributesWithQuestions: PropTypes.func.isRequired,
  clearAttributesWithQuestions: PropTypes.func.isRequired,
  getCourseEnrolledUsers: PropTypes.func.isRequired,
  getTagsCategories: PropTypes.func.isRequired,
  addMultipleTags: PropTypes.func.isRequired,
  clearTagsCategories: PropTypes.func.isRequired,
  getAllUsers: PropTypes.func.isRequired,
  clearAllUsers: PropTypes.func.isRequired,
};

export default withStyles(styles)(CourseDetailsPage);
